// UserDetail.cshtml (QOR staff): a staff account's role, access, and a new password — set here as in legacy (generated,
// shown once to copy and hand over), or chosen by the person from a code e-mailed to them.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { adminApi, type UserSummary } from '../api';
import { usePageTitle } from '../components/ConsoleLayout';
import { SET_PASSWORD_CONFIRM, ShownPassword } from '../components/ShownPassword';
import { TwoFactorReset } from '../components/TwoFactorReset';
import { confirmed } from '../components/ui';
import { PageHeading, ResultText, StatusBadge } from '../components/ui';
import { ROLE_OPTIONS } from '../scope';

const fieldStyle = { flex: 1 };

export function UserDetailPage() {
  const { id = '' } = useParams();
  const client = useQueryClient();
  const account = useQuery({
    queryKey: ['user', id],
    queryFn: async () => (await adminApi.users()).find((u) => u.id === id) ?? null,
  });
  usePageTitle(account.data?.displayName ?? 'User');
  const [outcome, setOutcome] = useState<{ message: string | null; ok: boolean }>({ message: null, ok: false });
  // kept here, not in UserForms: the forms are made again after every reload of the account
  const [shownPassword, setShownPassword] = useState<string | null>(null);

  async function after(ok: boolean, message: string, password?: string) {
    setOutcome({ ok, message });
    if (password) setShownPassword(password);
    await client.invalidateQueries({ queryKey: ['user', id] });
  }

  if (account.isPending) return null;
  const user = account.data ?? null;
  return (
    <>
      <PageHeading title={user?.displayName ?? 'User'} trail={[['Users', '/users']]} />
      <ResultText {...outcome} />
      {!user ? (
        <p className="qc-empty">User not found.</p>
      ) : (
        <>
          {shownPassword && (
            <ShownPassword
              email={user.email}
              password={shownPassword}
              onDone={() => {
                setShownPassword(null);
                setOutcome({ ok: true, message: "Password copied. It won't be shown again." });
              }}
            />
          )}
          <UserForms key={account.dataUpdatedAt} user={user} after={(ok, m, p) => void after(ok, m, p)} />
        </>
      )}
    </>
  );
}

function UserForms({
  user,
  after,
}: {
  user: UserSummary;
  after: (ok: boolean, message: string, password?: string) => void;
}) {
  const [role, setRole] = useState(user.role);
  const [busy, setBusy] = useState(false);
  const active = user.status === 'Active';

  async function saveRole(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    const r = await adminApi.setUserRole(user.id, role);
    setBusy(false);
    after(r.success, r.success ? 'Role updated.' : (r.error ?? "Couldn't update role."));
  }

  async function toggle() {
    setBusy(true);
    const r = await adminApi.setUserActive(user.id, !active);
    setBusy(false);
    after(r.success, r.success ? 'Status updated.' : (r.error ?? "Couldn't update status."));
  }

  async function setPassword() {
    if (!confirmed(SET_PASSWORD_CONFIRM(user.email))) return;
    setBusy(true);
    const r = await adminApi.setStaffPassword(user.id);
    setBusy(false);
    after(r.ok, r.ok ? 'A new password was set.' : r.error, r.ok ? r.data.password : undefined);
  }

  async function savePassword(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    const ok = await adminApi.sendStaffPasswordCode(user);
    setBusy(false);
    after(ok, ok ? `A code to choose a new password was e-mailed to ${user.email}.` : "Couldn't send a password code.");
  }

  return (
    <div className="qc-detail-row">
      <div className="qc-panel">
        <div className="qc-panel-title">
          <h3>Profile</h3>
          <span>{user.programName}</span>
        </div>
        <dl className="qc-detail-dl">
          <div>
            <dt>Email</dt>
            <dd>{user.email}</dd>
          </div>
          <div>
            <dt>Role</dt>
            <dd>{user.role}</dd>
          </div>
          <div>
            <dt>Status</dt>
            <dd>
              <StatusBadge status={user.status} />
            </dd>
          </div>
          <div>
            <dt>Password</dt>
            <dd>
              <span className={`qc-badge ${user.hasPassword ? 'qc-badge-success' : 'qc-badge-muted'}`}>
                {user.hasPassword ? 'Created' : 'Not created'}
              </span>
            </dd>
          </div>
        </dl>
      </div>

      <div className="qc-panel">
        <div className="qc-panel-title">
          <h3>Manage access</h3>
        </div>

        <div style={{ marginBottom: 24 }}>
          <p style={{ color: 'var(--qor-muted)', fontSize: '.8rem', fontWeight: 700, margin: '0 0 10px' }}>Role</p>
          <form onSubmit={(e) => void saveRole(e)} style={{ display: 'flex', gap: 10 }}>
            <select aria-label="Role" value={role} onChange={(e) => setRole(e.target.value)} style={fieldStyle}>
              {ROLE_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            <button type="submit" className="qc-button-ghost" disabled={busy}>
              Save
            </button>
          </form>
        </div>

        <div style={{ marginBottom: 24 }}>
          <p style={{ color: 'var(--qor-muted)', fontSize: '.8rem', fontWeight: 700, margin: '0 0 10px' }}>
            Account access
          </p>
          <button type="button" className="qc-button-ghost" disabled={busy} onClick={() => void toggle()}>
            {active ? 'Disable this account' : 'Enable this account'}
          </button>
        </div>

        <div>
          <p style={{ color: 'var(--qor-muted)', fontSize: '.8rem', fontWeight: 700, margin: '0 0 10px' }}>
            Set a new password
          </p>
          <form onSubmit={(e) => void savePassword(e)} style={{ display: 'flex', gap: 10 }}>
            <button type="button" className="qc-button-ghost" disabled={busy} onClick={() => void setPassword()}>
              Set password
            </button>
            <button type="submit" className="qc-button-ghost" disabled={busy}>
              E-mail a password code
            </button>
          </form>
          <TwoFactorReset kind="user" id={user.id} />
        </div>
      </div>
    </div>
  );
}
